import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import LayersIcon from '@mui/icons-material/Layers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import RefreshIcon from '@mui/icons-material/Refresh';

const formatFecha = (val) => {
  if (!val) return "—";
  const str = String(val);
  const part = str.includes("T") ? str.split("T")[0] : str;
  const parts = part.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return part;
};

const ControlHologramasSection = ({
  hologramaRecords = [],
  terminadoRecords = [],
  isFormOpen,
  setIsFormOpen,
  idEnvasadoTerminado,
  setIdEnvasadoTerminado,
  fechaMovimiento,
  setFechaMovimiento,
  marca,
  setMarca,
  loteEnvasado,
  setLoteEnvasado,
  capacidad,
  setCapacidad,
  invInicialRango,
  setInvInicialRango,
  invInicialCant,
  setInvInicialCant,
  entradaRango,
  setEntradaRango,
  entradaCant,
  setEntradaCant,
  salidaRango,
  setSalidaRango,
  salidaCant,
  setSalidaCant,
  invFinalRango,
  setInvFinalRango,
  invFinalCant,
  setInvFinalCant,
  mermasRango,
  setMermasRango,
  mermasCant,
  setMermasCant,
  observaciones,
  setObservaciones,
  handleSaveHologramas,
  onSaveInventarioHologramas,
  onReloadHologramas,
  onRegistrarMovimiento,
  onRegistrarMerma
}) => {
  // Modal de Movimientos de Hologramas (Salidas)
  const [isMovimientosModalOpen, setIsMovimientosModalOpen] = useState(false);
  const [selectedHologramaId, setSelectedHologramaId] = useState(null);

  // Calcular registros de inventario con salidas y saldos reales cruzados con movimientos y lotes terminados aprobados
  const inventarioCalculado = useMemo(() => {
    return (hologramaRecords || []).map(rec => {
      const fechaIngreso = rec.fecha_ingreso || rec.fechaIngreso || rec.fechaMovimiento || rec.createdAt;
      const prefijo = rec.prefijo || "A";
      const rIni = rec.rango_inicial || rec.rangoInicial || (rec.invInicialRango ? rec.invInicialRango.split("-")[0]?.trim() : "—");
      const rFin = rec.rango_final || rec.rangoFinal || (rec.invInicialRango && rec.invInicialRango.includes("-") ? rec.invInicialRango.split("-")[1]?.trim() : "—");
      const totalInicial = Number(rec.cantidad_inicial ?? rec.cantidadInicial ?? rec.invInicialCant ?? rec.cantidad ?? 0);
      const rangoCompleto = rec.rango_completo || rec.rangoCompleto || rec.invInicialRango || (rIni !== "—" && rFin !== "—" ? `${rIni} - ${rFin}` : rIni);

      const targetHoloId = rec.id_bitacora_hologramas || rec.id;
      const holoIniNum = parseInt(String(rIni).replace(/\D/g, ""), 10);
      const holoFinNum = parseInt(String(rFin).replace(/\D/g, ""), 10);

      // Movimientos directos de BD o locales (excluyendo entrada)
      const directMovs = (Array.isArray(rec.movimientos) ? rec.movimientos : [])
        .filter(m => {
          if (m.tipo_movimiento === "entrada") return false;
          if (m.id_bitacora_hologramas) {
            return String(m.id_bitacora_hologramas) === String(targetHoloId);
          }
          return true;
        });

      // Tipo de salida de un movimiento: viene del rango del lote terminado (o del motivo guardado en el kárdex)
      const normRango = (v) => String(v || "").replace(/\s+/g, "").toUpperCase();
      const tipoSalidaDeRango = (rComp, motivo, tipoMov) => {
        const objetivo = normRango(rComp);
        if (objetivo && objetivo !== "-") {
          for (const t of (terminadoRecords || [])) {
            for (const r of (t.rangosHologramas || t.holograma_rangos || [])) {
              const rr = r.rangoHologramas || r.rango_completo || (r.rango1 && r.rango2 ? `${r.prefijo || "A"}${r.rango1} - ${r.prefijo || "A"}${r.rango2}` : "");
              if (normRango(rr) === objetivo && (r.tipo_salida || r.tipoSalida)) return r.tipo_salida || r.tipoSalida;
            }
          }
        }
        const delMotivo = String(motivo || "").split(" - Lote")[0].trim();
        if (delMotivo && /venta|almac|merma|baja/i.test(delMotivo)) return delMotivo;
        if (/merma|baja/i.test(String(tipoMov || ""))) return "Baja o Merma";
        return "Hologramas adheridos";
      };

      // Buscar folios descontados en lotes terminados aprobados (V.Bo. Validado)
      const salidasTerminados = [];
      (terminadoRecords || []).forEach(term => {
        const loteAprobado = String(term.status || term.estatus || "").toLowerCase() === "aprobado";

        const rangos = term.rangosHologramas || term.holograma_rangos || [];
        rangos.forEach((r, rIdx) => {
          // Todos los rangos aparecen en el historial; solo los aceptados por la UI descuentan inventario
          const estatusRango = String(r.estatus || "").toLowerCase();
          const estatusMov = estatusRango === "aprobado" ? "aprobado"
            : estatusRango === "rechazado" ? "rechazado"
            : estatusRango ? "revision"
            : (loteAprobado ? "aprobado" : "revision");

          const rIniTerm = parseInt(String(r.rango1 || r.rango_inicial || (r.rangoHologramas ? r.rangoHologramas.split("-")[0] : "")).replace(/\D/g, ""), 10);
          const rFinTerm = parseInt(String(r.rango2 || r.rango_final || (r.rangoHologramas ? r.rangoHologramas.split("-")[1] : "")).replace(/\D/g, ""), 10);
          const rPref = (r.prefijo || "A").trim().toUpperCase();

          const hasNumericRange = !isNaN(holoIniNum) && !isNaN(holoFinNum) && !isNaN(rIniTerm) && !isNaN(rFinTerm);
          const rHoloId = r.id_bitacora_hologramas || r.idInventario;

          let isMatch = false;
          if (rHoloId) {
            // Asociación estricta por ID de inventario del lote
            isMatch = String(rHoloId) === String(targetHoloId);
          } else {
            // Solo para registros históricos sin ID de inventario
            const matchesByLote = rec.lote_envasado && term.loteEnvasado && rec.lote_envasado === term.loteEnvasado;
            const matchesExactRange = hasNumericRange && rPref === prefijo.trim().toUpperCase() && rIniTerm === holoIniNum && rFinTerm === holoFinNum;
            const matchesContainedInLote = matchesByLote && hasNumericRange && rPref === prefijo.trim().toUpperCase() && rIniTerm >= holoIniNum && rFinTerm <= holoFinNum;
            isMatch = matchesExactRange || matchesContainedInLote;
          }

          if (isMatch) {
            const cant = Number(r.cantidad || (rFinTerm >= rIniTerm ? rFinTerm - rIniTerm + 1 : 0));
            const rComp = r.rangoHologramas || (r.rango1 && r.rango2 ? `${rPref}${r.rango1} - ${rPref}${r.rango2}` : `${prefijo}${rIniTerm} - ${prefijo}${rFinTerm}`);
            salidasTerminados.push({
              id: `term-${term.id || term.id_bitacora_envasado}-${rIdx}`,
              id_bitacora_envasado: term.id || term.id_bitacora_envasado,
              loteEnvasado: term.loteEnvasado,
              rangoConsumido: rComp,
              rango_completo: rComp,
              cantidadConsumida: cant,
              cantidad: cant,
              rangoMermas: "N/A",
              cantidadMermada: 0,
              saldoRestanteCant: "-",
              fecha: term.fechaMovimiento || term.fecha_movimiento || new Date().toISOString().split('T')[0],
              tipo_movimiento: 'salida_adherido',
              motivo: estatusMov === "aprobado"
                ? `Hologramas adheridos al lote terminado ${term.loteEnvasado}`
                : `Salida pendiente de validación del lote ${term.loteEnvasado}`,
              observaciones: estatusMov === "aprobado"
                ? `Validado y aprobado por Unidad de Inspección - Lote: ${term.loteEnvasado}, Marca: ${term.marca || ''}`
                : (r.observaciones_correccion || `En espera del dictamen de la Unidad de Inspección - Lote: ${term.loteEnvasado}`),
              observaciones_correccion: estatusMov === "rechazado" ? r.observaciones_correccion : undefined,
              estatus: estatusMov,
              tipoSalida: r.tipo_salida || r.tipoSalida || tipoSalidaDeRango(rComp, '', ''),
              marca: term.marca
            });
          }
        });
      });

      // Unificar movimientos evitando duplicidad
      const salidasUnificadas = [];

      (directMovs || []).forEach((m, idx) => {
        const termRel = (terminadoRecords || []).find(t =>
          (m.id_bitacora_envasado && String(t.id || t.id_bitacora_envasado) === String(m.id_bitacora_envasado)) ||
          (m.motivo && t.loteEnvasado && m.motivo.includes(t.loteEnvasado))
        );

        const rCons = m.rangoConsumido || m.rango_completo || m.rangoCompleto || (m.rango_inicial ? `${m.prefijo || 'A'}${m.rango_inicial} - ${m.prefijo || 'A'}${m.rango_final}` : '-');
        const resolvedLote = (m.loteEnvasado && m.loteEnvasado !== "Inventario General" && m.loteEnvasado !== "-")
          ? m.loteEnvasado
          : (m.lote_envasado && m.lote_envasado !== "Inventario General" && m.lote_envasado !== "-")
            ? m.lote_envasado
            : (termRel?.loteEnvasado || (m.motivo ? m.motivo.match(/lote(?:\s+terminado)?\s+([A-Za-z0-9_\-]+)/i)?.[1] : null) || ((rec.loteEnvasado !== "Inventario General" && rec.lote_envasado !== "Inventario General") ? (rec.loteEnvasado || rec.lote_envasado) : null) || '-');

        const resolvedMarca = (m.marca && m.marca !== "General" && m.marca !== "-")
          ? m.marca
          : (termRel?.marca || ((rec.marca !== "General") ? rec.marca : null) || '-');

        // Evitar duplicados dentro de directMovs
        const existsDirect = salidasUnificadas.some(su =>
          (su.id && su.id === (m.id_movimiento_holograma || m.id)) ||
          (su.rangoConsumido && rCons !== '-' && su.rangoConsumido.trim().toUpperCase() === rCons.trim().toUpperCase())
        );

        if (!existsDirect) {
          salidasUnificadas.push({
            id: m.id_movimiento_holograma || m.id || `mov-${idx}`,
            id_bitacora_envasado: m.id_bitacora_envasado || termRel?.id || termRel?.id_bitacora_envasado || null,
            fecha: m.fecha ? (typeof m.fecha === 'string' ? m.fecha.split('T')[0] : new Date(m.fecha).toISOString().split('T')[0]) : (termRel?.fechaMovimiento || fechaIngreso || '-'),
            tipo_movimiento: m.tipo_movimiento || 'salida_adherido',
            rangoConsumido: rCons,
            rango_completo: rCons,
            cantidadConsumida: /^(merma|baja)$/i.test(m.tipo_movimiento || '') ? 0 : Number(m.cantidadConsumida ?? m.cantidad ?? 0),
            cantidad: Number(m.cantidadConsumida ?? m.cantidad ?? 0),
            rangoMermas: m.rangoMermas || m.rango_mermas || (/^(merma|baja)$/i.test(m.tipo_movimiento || '') ? rCons : 'N/A'),
            cantidadMermada: /^(merma|baja)$/i.test(m.tipo_movimiento || '') ? Number(m.cantidad ?? 0) : Number(m.cantidadMermada ?? m.cantidad_mermada ?? 0),
            saldoRestanteCant: m.saldoRestanteCant ?? m.saldo_restante ?? '-',
            motivo: m.motivo || `Hologramas adheridos al lote terminado ${resolvedLote !== '-' ? resolvedLote : ''}`,
            observaciones: m.observaciones || `Validado y aprobado por Unidad de Inspección - Lote: ${resolvedLote !== '-' ? resolvedLote : ''}, Marca: ${resolvedMarca !== '-' ? resolvedMarca : ''}`,
            estatus: m.estatus || 'aprobado',
            observaciones_correccion: m.observaciones_correccion,
            tipoSalida: m.tipo_salida || m.tipoSalida || tipoSalidaDeRango(rCons, m.motivo, m.tipo_movimiento),
            loteEnvasado: resolvedLote,
            marca: resolvedMarca
          });
        }
      });

      salidasTerminados.forEach(st => {
        // Un mismo rango de folios es un mismo movimiento (el nombre del lote o la cantidad pueden repetirse entre salidas distintas)
        const normR = (v) => String(v || "").replace(/\s+/g, "").toUpperCase();
        const matchIdx = salidasUnificadas.findIndex(su =>
          (su.id && su.id === st.id) ||
          (normR(su.rangoConsumido) && normR(su.rangoConsumido) === normR(st.rangoConsumido))
        );

        if (matchIdx === -1) {
          salidasUnificadas.push(st);
        } else {
          // Si ya existe, enriquecerlo con los datos completos del terminado
          const existing = salidasUnificadas[matchIdx];
          salidasUnificadas[matchIdx] = {
            ...existing,
            loteEnvasado: (st.loteEnvasado && st.loteEnvasado !== "-" && st.loteEnvasado !== "Inventario General") ? st.loteEnvasado : existing.loteEnvasado,
            marca: (st.marca && st.marca !== "-" && st.marca !== "General") ? st.marca : existing.marca,
            tipoSalida: existing.tipoSalida || st.tipoSalida,
            motivo: existing.motivo || st.motivo,
            observaciones: existing.observaciones || st.observaciones,
            fecha: existing.fecha && existing.fecha !== "-" ? existing.fecha : st.fecha
          };
        }
      });

      // Calcular saldo restante para cada movimiento si no lo tiene
      let runningSaldo = totalInicial;
      salidasUnificadas.forEach(mov => {
        if (mov.estatus !== "aprobado") return; // pendientes/rechazados no descuentan ni llevan saldo
        runningSaldo = Math.max(0, runningSaldo - (Number(mov.cantidadConsumida) || 0) - (Number(mov.cantidadMermada) || 0));
        if (mov.saldoRestanteCant === "-" || mov.saldoRestanteCant === undefined || mov.saldoRestanteCant === null) {
          mov.saldoRestanteCant = runningSaldo;
        }
      });

      // Total consumido / salida
      const sumSalidas = salidasUnificadas
        .filter(curr => curr.estatus === "aprobado")
        .reduce((acc, curr) => acc + (Number(curr.cantidadConsumida) || 0), 0);
      const totalSalida = Math.max(
        Number(rec.cantidad_utilizada ?? rec.cantidadUtilizada ?? rec.salidaCant ?? 0),
        sumSalidas
      );

      // Saldo disponible real
      const rawDisponible = rec.cantidad_disponible ?? rec.cantidadDisponible ?? rec.invFinalCant;
      const isExplicitAgotado = String(rec.estatus || "").toLowerCase() === "agotado";
      let saldoActual = 0;
      if (!isExplicitAgotado) {
        if (rawDisponible !== undefined && rawDisponible !== null && Number(rawDisponible) < totalInicial) {
          saldoActual = Math.min(Number(rawDisponible), Math.max(0, totalInicial - totalSalida));
        } else {
          saldoActual = Math.max(0, totalInicial - totalSalida);
        }
      }

      // Formatear rango consumido y rango disponible
      let salidaRangoDisp = "Sin salidas";
      let saldoRangoDisp = rec.rango_disponible || rec.invFinalRango || rangoCompleto;

      if (totalSalida > 0) {
        if (salidasUnificadas.length > 0) {
          salidaRangoDisp = Array.from(new Set(salidasUnificadas.filter(s => s.estatus === "aprobado").map(s => s.rangoConsumido).filter(Boolean))).join(", ");
        } else {
          salidaRangoDisp = `${totalSalida} folios aplicados`;
        }

        if (!isNaN(holoIniNum) && !isNaN(holoFinNum) && saldoActual > 0) {
          const nuevoFolioIni = holoIniNum + totalSalida;
          if (nuevoFolioIni <= holoFinNum) {
            const padLen = String(rIni).replace(/\D/g, "").length;
            saldoRangoDisp = `${prefijo}${String(nuevoFolioIni).padStart(padLen, "0")} - ${prefijo}${String(holoFinNum).padStart(padLen, "0")}`;
          }
        } else if (saldoActual <= 0) {
          saldoRangoDisp = "Folios agotados";
        }
      }

      const statusTexto = saldoActual <= 0 ? "agotado" : (rec.estatus || rec.status || "activo");
      const loteRef = (rec.lote_envasado || rec.loteEnvasado) && (rec.lote_envasado !== "Inventario General" && rec.loteEnvasado !== "Inventario General")
        ? (rec.lote_envasado || rec.loteEnvasado)
        : null;

      return {
        ...rec,
        fechaIngreso,
        prefijo,
        rIni,
        rFin,
        totalInicial,
        rangoCompleto,
        totalSalida,
        salidaCant: totalSalida,
        cantidad_utilizada: totalSalida,
        cantidadUtilizada: totalSalida,
        salidaRango: salidaRangoDisp,
        salidaRangoDisp,
        saldoActual,
        cantidad_disponible: saldoActual,
        cantidadDisponible: saldoActual,
        invFinalCant: saldoActual,
        saldoRangoDisp,
        invFinalRango: saldoRangoDisp,
        statusTexto,
        loteRef,
        movimientos: salidasUnificadas
      };
    });
  }, [hologramaRecords, terminadoRecords]);

  // El inventario abierto se toma en vivo del listado, así lo aceptado en Unidad de Inspección se refleja al actualizar
  const selectedHolograma = useMemo(() => {
    if (selectedHologramaId === null || selectedHologramaId === undefined) return null;
    return (inventarioCalculado || []).find(c => String(c.id_bitacora_hologramas || c.id) === String(selectedHologramaId)) || null;
  }, [inventarioCalculado, selectedHologramaId]);

  // Movimientos correspondientes a este lote de hologramas
  const movimientosDelLote = useMemo(() => {
    if (!selectedHolograma) return [];
    return Array.isArray(selectedHolograma.movimientos) ? selectedHolograma.movimientos : [];
  }, [selectedHolograma]);

  // Auto-fill fields if Finished Bottling record is selected
  useEffect(() => {
    if (idEnvasadoTerminado && idEnvasadoTerminado !== "manual") {
      const selected = terminadoRecords.find(r => r.id === idEnvasadoTerminado);
      if (selected) {
        setMarca(selected.marca);
        setLoteEnvasado(selected.loteEnvasado);
        setCapacidad(selected.capacidad);
      }
    }
  }, [idEnvasadoTerminado, terminadoRecords, setMarca, setLoteEnvasado, setCapacidad]);

  // Abrir modal de movimientos para un registro específico
  const handleOpenMovimientosModal = (rec) => {
    setSelectedHologramaId(rec.id_bitacora_hologramas || rec.id);
    setIsMovimientosModalOpen(true);
    if (onReloadHologramas) onReloadHologramas();
  };

  // Mientras el modal está abierto se actualiza solo (lo que acepte la Unidad de Inspección aparece sin recargar)
  useEffect(() => {
    if (!isMovimientosModalOpen || !onReloadHologramas) return undefined;
    const timer = setInterval(() => onReloadHologramas(), 15000);
    return () => clearInterval(timer);
  }, [isMovimientosModalOpen, onReloadHologramas]);



  // ==========================================
  // ESTADOS DEL MODAL: INVENTARIO DE HOLOGRAMAS
  // ==========================================
  const [isInventarioModalOpen, setIsInventarioModalOpen] = useState(false);
  const invPrefijo = "A"; // Bloqueado en A
  const [invRangoInicial, setInvRangoInicial] = useState("");
  const [invRangoFinal, setInvRangoFinal] = useState("");
  const invFecha = new Date().toISOString().split("T")[0]; // Automático
  const [invObservaciones, setInvObservaciones] = useState("");
  const [isSavingInventario, setIsSavingInventario] = useState(false);

  // Cálculos dinámicos de rango y piezas
  const cleanNumIni = parseInt(String(invRangoInicial || "").replace(/[^\d]/g, ""), 10) || 0;
  const cleanNumFin = parseInt(String(invRangoFinal || "").replace(/[^\d]/g, ""), 10) || 0;
  const invCalculatedCant = (cleanNumFin >= cleanNumIni && cleanNumFin > 0 && cleanNumIni > 0)
    ? (cleanNumFin - cleanNumIni + 1)
    : 0;
  const invRangoCompleto = (cleanNumIni > 0 && cleanNumFin > 0)
    ? `A${cleanNumIni} - A${cleanNumFin}`
    : (cleanNumIni > 0 ? `A${cleanNumIni}` : "");

  const handleOpenInventarioModal = () => {
    setIsInventarioModalOpen(true);
  };

  const handleCloseInventarioModal = () => {
    setIsInventarioModalOpen(false);
    setInvRangoInicial("");
    setInvRangoFinal("");
    setInvObservaciones("");
  };

  const handleGuardarInventario = async (e) => {
    if (e) e.preventDefault();

    if (!cleanNumIni || !cleanNumFin) {
      Swal.fire({
        icon: "warning",
        title: "Rangos Incompletos",
        text: "Debe ingresar el Rango Inicial y el Rango Final con números de folio válidos.",
        confirmButtonColor: "#D97706"
      });
      return;
    }

    if (cleanNumFin < cleanNumIni) {
      Swal.fire({
        icon: "error",
        title: "Rango Inválido",
        text: "El Rango Final debe ser mayor o igual al Rango Inicial.",
        confirmButtonColor: "#DC2626"
      });
      return;
    }

    try {
      setIsSavingInventario(true);
      const payload = {
        prefijo: "A",
        rango_inicial: `A${cleanNumIni}`,
        rango_final: `A${cleanNumFin}`,
        rango_completo: invRangoCompleto,
        cantidad_inicial: invCalculatedCant,
        fecha_ingreso: invFecha,
        marca: "General",
        lote_envasado: "Inventario General",
        capacidad: "N/A",
        categoria: "Mezcal",
        clase: "N/A",
        observaciones: invObservaciones.trim(),
        origen_registro: "manual"
      };

      if (onSaveInventarioHologramas) {
        await onSaveInventarioHologramas(payload);
      } else {
        Swal.fire({
          icon: "success",
          title: "Inventario Registrado",
          text: `Rango ${invRangoCompleto} (${invCalculatedCant} piezas) guardado.`,
          confirmButtonColor: "#059669"
        });
      }

      setIsInventarioModalOpen(false);
      setInvRangoInicial("");
      setInvRangoFinal("");
      setInvObservaciones("");
    } catch (err) {
      console.error("Error al guardar inventario de hologramas:", err);
    } finally {
      setIsSavingInventario(false);
    }
  };

  // ==========================================
  // ESTADOS DEL MODAL: MERMAS DE HOLOGRAMAS
  // ==========================================
  const [isMermaModalOpen, setIsMermaModalOpen] = useState(false);
  const [mermaInvId, setMermaInvId] = useState("");
  const [mermaIni, setMermaIni] = useState("");
  const [mermaFin, setMermaFin] = useState("");
  const [mermaMotivo, setMermaMotivo] = useState("");
  const [isSavingMerma, setIsSavingMerma] = useState(false);

  const soloNum = (v) => parseInt(String(v || "").replace(/\D/g, ""), 10);
  const esMermaMov = (m) => /^(merma|baja)$/i.test(m?.tipo_movimiento || "");

  // Inventarios con saldo libre: lo ya apartado por mermas en revisión no se puede volver a solicitar
  const inventariosParaMerma = useMemo(() => inventarioCalculado.map(inv => {
    const apartado = (inv.movimientos || [])
      .filter(m => esMermaMov(m) && ["revision", "pendiente"].includes(String(m.estatus).toLowerCase()))
      .reduce((acc, m) => acc + (Number(m.cantidad) || 0), 0);
    return { ...inv, saldoLibre: Math.max(0, (Number(inv.saldoActual) || 0) - apartado) };
  }).filter(inv => inv.saldoLibre > 0), [inventarioCalculado]);

  const mermaInv = inventariosParaMerma.find(i => String(i.id_bitacora_hologramas || i.id) === String(mermaInvId));
  const mermaIniNum = soloNum(mermaIni);
  const mermaFinNum = soloNum(mermaFin);
  const mermaCant = (!isNaN(mermaIniNum) && !isNaN(mermaFinNum) && mermaFinNum >= mermaIniNum) ? mermaFinNum - mermaIniNum + 1 : 0;

  const handleCloseMermaModal = () => {
    setIsMermaModalOpen(false);
    setMermaInvId("");
    setMermaIni("");
    setMermaFin("");
    setMermaMotivo("");
  };

  const handleGuardarMerma = async (e) => {
    if (e) e.preventDefault();
    if (isSavingMerma) return;
    const warn = (title, text) => Swal.fire({ icon: "warning", title, text, confirmButtonColor: "#DC2626" });
    if (!mermaInv) return warn("Inventario requerido", "Seleccione el inventario de hologramas del que se descontará la merma.");
    if (isNaN(mermaIniNum) || isNaN(mermaFinNum)) return warn("Rango incompleto", "Indique el folio inicial y el folio final de la merma.");
    if (mermaFinNum < mermaIniNum) return warn("Rango inválido", "El folio final debe ser mayor o igual al folio inicial.");
    const invIni = soloNum(mermaInv.rIni);
    const invFin = soloNum(mermaInv.rFin);
    if (mermaIniNum < invIni || mermaFinNum > invFin) {
      return warn("Rango fuera del inventario", `El rango de la merma debe estar dentro de ${mermaInv.rangoCompleto}.`);
    }
    if (mermaCant > mermaInv.saldoLibre) {
      return warn("Saldo insuficiente", `El inventario solo tiene ${mermaInv.saldoLibre} folios disponibles para merma.`);
    }
    const pad = String(mermaInv.rIni).replace(/\D/g, "").length;
    const pref = mermaInv.prefijo || "A";
    const fmt = (n) => String(n).padStart(pad, "0");
    const payload = {
      id_bitacora_hologramas: mermaInv.id_bitacora_hologramas || mermaInv.id,
      prefijo: pref,
      rango_inicial: `${pref}${fmt(mermaIniNum)}`,
      rango_final: `${pref}${fmt(mermaFinNum)}`,
      rango_completo: `${pref}${fmt(mermaIniNum)} - ${pref}${fmt(mermaFinNum)}`,
      cantidad: mermaCant,
      motivo: "Merma de hologramas",
      observaciones: mermaMotivo.trim() || null
    };
    setIsSavingMerma(true);
    const resultado = onRegistrarMerma ? await onRegistrarMerma(payload) : { ok: false, error: "Función no disponible." };
    setIsSavingMerma(false);
    if (resultado && resultado.ok === false) {
      Swal.fire({ icon: "error", title: "No se pudo registrar la merma", text: String(resultado.error), confirmButtonColor: "#DC2626" });
      return;
    }
    handleCloseMermaModal();
    Swal.fire({
      icon: "success",
      title: "Merma registrada",
      html: `Se registró la merma <strong>${payload.rango_completo}</strong> (${mermaCant} folios).<br/><br/>Queda <strong>en revisión</strong> por la Unidad de Inspección; el saldo del inventario se descuenta cuando la acepte.`,
      confirmButtonColor: "#059669"
    });
  };

  if (!isFormOpen) {
    return (
      <div className="animate-fadeIn">
        {/* HEADER TOOLBAR DE LA SECCIÓN CON BOTÓN NUEVO INVENTARIO */}
        <div
          className="history-section-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "20px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{
              width: "44px",
              height: "44px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(5, 150, 105, 0.15) 0%, rgba(4, 120, 87, 0.25) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#059669"
            }}>
              <ConfirmationNumberIcon style={{ fontSize: "26px" }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "1.3rem", fontWeight: 800, color: "#1E293B" }}>
                Control del Inventario de Hologramas (Sección V)
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: "12.5px", color: "#64748B" }}>
                Gestión manual de folios autorizados, registro de entradas y control de salidas
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              type="button"
              className="btn-registro"
              onClick={handleOpenInventarioModal}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "10px",
                padding: "10px 20px",
                fontSize: "13.5px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(5, 150, 105, 0.3)",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 18px rgba(5, 150, 105, 0.4)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 14px rgba(5, 150, 105, 0.3)";
              }}
            >
              <AddIcon style={{ fontSize: "20px" }} />
              + Inventario de Hologramas
            </button>

            <button
              type="button"
              onClick={() => setIsMermaModalOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "10px",
                padding: "10px 20px",
                fontSize: "13.5px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(220, 38, 38, 0.3)"
              }}
            >
              <TrendingDownIcon style={{ fontSize: "20px" }} />
              Mermas
            </button>

            {onReloadHologramas && (
              <button
                type="button"
                onClick={onReloadHologramas}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#F8FAFC",
                  color: "#475569",
                  border: "1px solid #CBD5E1",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
                title="Actualizar listado de hologramas"
                onMouseEnter={e => {
                  e.currentTarget.style.background = "#F1F5F9";
                  e.currentTarget.style.borderColor = "#94A3B8";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "#F8FAFC";
                  e.currentTarget.style.borderColor = "#CBD5E1";
                }}
              >
                <RefreshIcon style={{ fontSize: "19px" }} />
              </button>
            )}
          </div>
        </div>

        {/* TABLA DE REGISTROS DE HOLOGRAMAS */}
        <div className="records-list-wrapper">
          <table className="records-table">
            <thead>
              <tr>
                <th style={{ width: "115px", textAlign: "center" }}>Fecha Ingreso</th>
                <th style={{ width: "70px", textAlign: "center" }}>Prefijo</th>
                <th style={{ width: "110px" }}>Rango Inicial</th>
                <th style={{ width: "110px" }}>Rango Final</th>
                <th>Total Asignados</th>

                {/* Control Hologramas */}
                <th>Consumidos</th>
                <th>Saldo Disponible</th>

                {/* Trazabilidad */}
                <th style={{ width: "95px", textAlign: "center" }}>Estado</th>

                {/* Acciones */}
                <th style={{ width: "160px", textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {inventarioCalculado.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "40px 20px", color: "#64748B" }}>
                    <ConfirmationNumberIcon style={{ fontSize: "40px", color: "#CBD5E1", display: "block", margin: "0 auto 8px" }} />
                    <p style={{ margin: 0, fontWeight: 600, fontSize: "14px" }}>No hay registros de inventario de hologramas aún.</p>
                    <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#94A3B8" }}>
                      Haga clic en <strong>"+ Inventario de Hologramas"</strong> para registrar un nuevo rango de folios.
                    </p>
                  </td>
                </tr>
              ) : (
                inventarioCalculado.map((rec, i) => {
                  const {
                    fechaIngreso,
                    prefijo,
                    rIni,
                    rFin,
                    totalInicial,
                    rangoCompleto,
                    salidaRangoDisp,
                    totalSalida,
                    saldoRangoDisp,
                    saldoActual,
                    statusTexto,
                    loteRef
                  } = rec;

                  return (
                    <tr key={rec.id_bitacora_hologramas || rec.id || i}>
                      <td style={{ textAlign: "center", fontWeight: 600, color: "#334155", whiteSpace: "nowrap" }}>
                        {formatFecha(fechaIngreso)}
                      </td>

                      <td style={{ textAlign: "center" }}>
                        <span style={{
                          display: "inline-block",
                          padding: "2px 10px",
                          borderRadius: "6px",
                          background: "rgba(5, 150, 105, 0.12)",
                          color: "#059669",
                          fontWeight: 800,
                          fontSize: "12.5px",
                          letterSpacing: "0.5px"
                        }}>
                          {prefijo}
                        </span>
                      </td>

                      <td style={{ fontWeight: 700, color: "#1E293B" }}>
                        {rIni}
                      </td>

                      <td style={{ fontWeight: 700, color: "#1E293B" }}>
                        {rFin}
                      </td>

                      <td>
                        <div style={{ fontWeight: 800, fontSize: "13px", color: "#065F46" }}>
                          {rangoCompleto}
                        </div>
                        <div style={{ fontSize: "11px", color: "#64748B", fontWeight: 600 }}>
                          {totalInicial} asignados
                          {loteRef && <span style={{ marginLeft: "6px", color: "#B45309" }}>• {loteRef}</span>}
                        </div>
                      </td>

                      <td>
                        <div style={{ fontWeight: 800, fontSize: "13px", color: totalSalida > 0 ? "#D97706" : "#64748B" }}>
                          {totalSalida} aplicados
                        </div>
                      </td>

                      <td>
                        <div style={{ fontWeight: 800, fontSize: "13px", color: "#059669" }}>
                          {saldoRangoDisp}
                        </div>
                        <div style={{ fontSize: "11px", color: "#059669", fontWeight: 700 }}>
                          {saldoActual} disponibles
                        </div>
                      </td>

                      <td style={{ textAlign: "center" }}>
                        <span className={`badge-status ${statusTexto === "aprobado" || statusTexto === "activo" ? "aprobado" : statusTexto === "revision" ? "revision" : "borrador"}`}>
                          {statusTexto === "aprobado" || statusTexto === "activo" ? "Activo" : statusTexto === "revision" ? "En revisión" : "Borrador"}
                        </span>
                      </td>

                      <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          onClick={() => handleOpenMovimientosModal(rec)}
                          style={{
                            background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: "8px",
                            padding: "6px 12px",
                            fontSize: "12px",
                            fontWeight: 700,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            boxShadow: "0 2px 6px rgba(5, 150, 105, 0.25)",
                            transition: "all 0.2s ease"
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = "0 4px 10px rgba(5, 150, 105, 0.35)";
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 2px 6px rgba(5, 150, 105, 0.25)";
                          }}
                          title="Ver y registrar salidas de hologramas por rango"
                        >
                          <TrendingDownIcon style={{ fontSize: 16 }} />
                          <span>Movimientos de Hologramas</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* MODAL MERMAS: DESCUENTO DE UN RANGO DE FOLIOS DE UN INVENTARIO (VALIDA LA UI) */}
        {isMermaModalOpen && createPortal(
          <div
            style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.65)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999999, padding: "20px" }}
            onClick={(e) => { if (e.target === e.currentTarget) handleCloseMermaModal(); }}
          >
            <div style={{ backgroundColor: "#FFFFFF", borderRadius: "18px", width: "100%", maxWidth: "560px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}>
              <div style={{ padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)", borderTopLeftRadius: "18px", borderTopRightRadius: "18px", color: "#FFFFFF" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800 }}>Registrar Merma de Hologramas</h3>
                  <p style={{ margin: "2px 0 0", fontSize: "12px", opacity: 0.9 }}>Se descuenta del inventario cuando la Unidad de Inspección la acepte</p>
                </div>
                <button type="button" onClick={handleCloseMermaModal} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "32px", height: "32px", color: "#FFFFFF", cursor: "pointer" }}>
                  <CloseIcon style={{ fontSize: 20 }} />
                </button>
              </div>

              <form onSubmit={handleGuardarMerma} style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: "14px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#991B1B", marginBottom: "5px" }}>Inventario del que se descontará *</label>
                  <select
                    value={mermaInvId}
                    onChange={e => { setMermaInvId(e.target.value); setMermaIni(""); setMermaFin(""); }}
                    className="custom-select-field"
                    style={{ width: "100%" }}
                  >
                    <option value="">-- Seleccionar inventario --</option>
                    {inventariosParaMerma.map(inv => (
                      <option key={inv.id_bitacora_hologramas || inv.id} value={inv.id_bitacora_hologramas || inv.id}>
                        {inv.rangoCompleto} · disponibles: {inv.saldoLibre}
                      </option>
                    ))}
                  </select>
                  {inventariosParaMerma.length === 0 && (
                    <div style={{ marginTop: 6, fontSize: "12px", color: "#DC2626" }}>No hay inventarios con folios disponibles.</div>
                  )}
                </div>

                {mermaInv && (
                  <>
                    <div style={{ fontSize: "12px", color: "#64748B" }}>
                      Rango del inventario: <strong>{mermaInv.rangoCompleto}</strong> · Folios disponibles: <strong>{mermaInv.saldoLibre}</strong>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#991B1B", marginBottom: "5px" }}>Folio inicial de la merma *</label>
                        <input value={mermaIni} onChange={e => setMermaIni(e.target.value.replace(/\D/g, ""))} className="custom-input-field" style={{ width: "100%" }} placeholder="Ej. 1005" />
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#991B1B", marginBottom: "5px" }}>Folio final de la merma *</label>
                        <input value={mermaFin} onChange={e => setMermaFin(e.target.value.replace(/\D/g, ""))} className="custom-input-field" style={{ width: "100%" }} placeholder="Ej. 1010" />
                      </div>
                    </div>
                    <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#991B1B" }}>
                      Folios a dar de baja: <strong>{mermaCant}</strong>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#991B1B", marginBottom: "5px" }}>Motivo / Observaciones</label>
                      <textarea value={mermaMotivo} onChange={e => setMermaMotivo(e.target.value)} rows={2} className="custom-input-field" style={{ width: "100%" }} placeholder="Ej. Hologramas dañados al aplicar" />
                    </div>
                  </>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "4px" }}>
                  <button type="button" className="btn-secondary-custom" onClick={handleCloseMermaModal} disabled={isSavingMerma}>Cancelar</button>
                  <button
                    type="submit"
                    disabled={isSavingMerma || !mermaInv || mermaCant <= 0}
                    style={{ padding: "9px 20px", background: "#DC2626", color: "#FFFFFF", border: "none", borderRadius: "8px", fontWeight: 700, cursor: "pointer", opacity: (isSavingMerma || !mermaInv || mermaCant <= 0) ? 0.5 : 1 }}
                  >
                    {isSavingMerma ? "Guardando..." : "Registrar merma"}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* MODAL 1: REGISTRAR INVENTARIO DE HOLOGRAMAS (NUEVO REGISTRO MANUAL) */}
        {isInventarioModalOpen && createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.65)",
              backdropFilter: "blur(6px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999999,
              padding: "20px"
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseInventarioModal();
            }}
          >
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "18px",
                width: "100%",
                maxWidth: "680px",
                maxHeight: "92vh",
                overflowY: "auto",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                display: "flex",
                flexDirection: "column"
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header del Modal */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  borderTopLeftRadius: "18px",
                  borderTopRightRadius: "18px",
                  color: "#FFFFFF"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "10px",
                    background: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <ConfirmationNumberIcon style={{ color: "#FFFFFF", fontSize: 24 }} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: "17.5px", fontWeight: 800 }}>
                      Inventario de Hologramas
                    </h3>
                    <p style={{ margin: "2px 0 0", fontSize: "12px", opacity: 0.9 }}>
                      Registrar nuevo rango inicial y rango final con prefijo oficial
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCloseInventarioModal}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    border: "none",
                    borderRadius: "8px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#FFFFFF",
                    cursor: "pointer"
                  }}
                >
                  <CloseIcon style={{ fontSize: 20 }} />
                </button>
              </div>

              {/* Formulario del Modal */}
              <form onSubmit={handleGuardarInventario} style={{ padding: "24px" }}>
                {/* Resumen del Rango Calculado */}
                <div style={{
                  background: "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)",
                  border: "1px solid #A7F3D0",
                  borderRadius: "14px",
                  padding: "16px 20px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px"
                }}>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#065F46" }}>
                      Vista Previa del Rango Autorizado
                    </span>
                    <div style={{ fontSize: "19px", fontWeight: 900, color: "#047857", marginTop: "2px" }}>
                      {invRangoCompleto || "Ingrese Prefijo y Rangos"}
                    </div>
                  </div>
                  <div style={{
                    background: "#FFFFFF",
                    padding: "8px 16px",
                    borderRadius: "10px",
                    border: "1px solid #6EE7B7",
                    textAlign: "right"
                  }}>
                    <span style={{ fontSize: "11px", color: "#64748B", display: "block" }}>Total de Hologramas</span>
                    <span style={{ fontSize: "18px", fontWeight: 900, color: "#059669" }}>
                      {invCalculatedCant} <small style={{ fontSize: "12px", fontWeight: 600 }}>piezas</small>
                    </span>
                  </div>
                </div>

                {/* FILA 1: PREFIJO (BLOQUEADO EN A), RANGO INICIAL, RANGO FINAL */}
                <div style={{ display: "grid", gridTemplateColumns: "110px 1fr 1fr", gap: "16px", marginBottom: "18px" }}>
                  <div>
                    <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
                      Prefijo * <span style={{ fontSize: "10px", color: "#64748B", fontWeight: 600 }}>(Fijo)</span>
                    </label>
                    <input
                      type="text"
                      value="A"
                      disabled
                      readOnly
                      title="El prefijo oficial está bloqueado en serie A"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1.5px solid #CBD5E1",
                        background: "#F1F5F9",
                        color: "#0F172A",
                        fontSize: "15px",
                        fontWeight: 800,
                        textAlign: "center",
                        cursor: "not-allowed",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
                      Rango Inicial (Folio) *
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={invRangoInicial}
                      onChange={e => setInvRangoInicial(e.target.value)}
                      placeholder="Ej. 1000"
                      required
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1.5px solid #CBD5E1",
                        fontSize: "14px",
                        fontWeight: 600,
                        boxSizing: "border-box"
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
                      Rango Final (Folio) *
                    </label>
                    <input
                      type="number"
                      min={cleanNumIni || 1}
                      value={invRangoFinal}
                      onChange={e => setInvRangoFinal(e.target.value)}
                      placeholder="Ej. 1500"
                      required
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1.5px solid #CBD5E1",
                        fontSize: "14px",
                        fontWeight: 600,
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                </div>

                {/* FILA 2: FECHA DE INGRESO AUTOMÁTICO */}
                <div style={{ marginBottom: "18px" }}>
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
                    <span>Fecha de Ingreso Automático</span>
                    <span style={{ fontSize: "11px", color: "#059669", background: "#ECFDF5", border: "1px solid #A7F3D0", padding: "2px 8px", borderRadius: "6px", fontWeight: 700 }}>
                      ✓ Automático (Fecha Actual)
                    </span>
                  </label>
                  <input
                    type="date"
                    value={invFecha}
                    disabled
                    readOnly
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1.5px solid #CBD5E1",
                      background: "#F8FAFC",
                      color: "#475569",
                      fontSize: "13.5px",
                      fontWeight: 600,
                      cursor: "not-allowed",
                      boxSizing: "border-box"
                    }}
                  />
                </div>

                {/* FILA 3: OBSERVACIONES / NOTAS DE ENTREGA */}
                <div style={{ marginBottom: "24px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
                    Observaciones / Notas de Entrega
                  </label>
                  <textarea
                    rows={3}
                    value={invObservaciones}
                    onChange={e => setInvObservaciones(e.target.value)}
                    placeholder="Número de remisión, paquete o notas del lote de hologramas..."
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1.5px solid #CBD5E1",
                      fontSize: "13.5px",
                      boxSizing: "border-box",
                      resize: "vertical"
                    }}
                  />
                </div>

                {/* BOTONES DE ACCIÓN */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", borderTop: "1px solid #E2E8F0", paddingTop: "18px" }}>
                  <button
                    type="button"
                    onClick={handleCloseInventarioModal}
                    disabled={isSavingInventario}
                    style={{
                      padding: "10px 18px",
                      borderRadius: "8px",
                      border: "1px solid #CBD5E1",
                      background: "#F8FAFC",
                      color: "#475569",
                      fontSize: "13.5px",
                      fontWeight: 600,
                      cursor: "pointer"
                    }}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    disabled={isSavingInventario || invCalculatedCant <= 0}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 22px",
                      borderRadius: "8px",
                      border: "none",
                      background: (isSavingInventario || invCalculatedCant <= 0)
                        ? "#94A3B8"
                        : "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      color: "#FFFFFF",
                      fontSize: "13.5px",
                      fontWeight: 700,
                      cursor: (isSavingInventario || invCalculatedCant <= 0) ? "not-allowed" : "pointer",
                      boxShadow: (isSavingInventario || invCalculatedCant <= 0)
                        ? "none"
                        : "0 4px 12px rgba(5, 150, 105, 0.3)"
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: "18px" }} />
                    {isSavingInventario ? "Guardando..." : "Guardar en Inventario"}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* MODAL 2: MOVIMIENTOS DE HOLOGRAMAS (SALIDAS POR RANGO) */}
        {isMovimientosModalOpen && selectedHolograma && createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.65)",
              backdropFilter: "blur(6px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999999,
              padding: "20px"
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsMovimientosModalOpen(false);
            }}
          >
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "850px",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column"
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header del Modal */}
              <div
                style={{
                  padding: "18px 24px",
                  borderBottom: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  borderTopLeftRadius: "16px",
                  borderTopRightRadius: "16px",
                  color: "#FFFFFF"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <ConfirmationNumberIcon style={{ color: "#FFFFFF", fontSize: 24 }} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800 }}>
                      Movimientos de Hologramas (Salidas por Rango)
                    </h3>
                    <p style={{ margin: "2px 0 0 0", fontSize: "12.5px", opacity: 0.9 }}>
                      Ref: <strong>{selectedHolograma.idEnvasadoTerminado || selectedHolograma.id_bitacora_hologramas || selectedHolograma.id}</strong> | Marca: <strong>{selectedHolograma.marca || 'General'}</strong> | Lote: <strong>{selectedHolograma.loteEnvasado || 'Lotes de Envasado'}</strong> ({selectedHolograma.capacidad || '750 ml'})
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsMovimientosModalOpen(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#FFFFFF",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"}
                >
                  <CloseIcon style={{ fontSize: 18 }} />
                </button>
              </div>

              {/* Resumen del Balance de Hologramas */}
              <div style={{ padding: "16px 24px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px" }}>
                  <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Rango Asignado Inicial</div>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#1E293B", marginTop: "3px" }}>
                      {selectedHolograma.invInicialRango || selectedHolograma.rango_completo || selectedHolograma.rangoCompleto || "N/A"}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748B" }}>
                      Total: {selectedHolograma.invInicialCant ?? selectedHolograma.cantidad_inicial ?? selectedHolograma.cantidadInicial ?? 0} pzs
                    </div>
                  </div>

                  <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#D97706", textTransform: "uppercase" }}>Salidas Acumuladas</div>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#D97706", marginTop: "3px" }}>
                      {selectedHolograma.salidaCant ?? selectedHolograma.cantidad_utilizada ?? selectedHolograma.cantidadUtilizada ?? 0} pzs
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748B" }}>
                      {selectedHolograma.salidaRango || (Number(selectedHolograma.salidaCant || selectedHolograma.cantidad_utilizada || 0) > 0 ? "Folios aplicados" : "Sin registros")}
                    </div>
                  </div>

                  <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#DC2626", textTransform: "uppercase" }}>Mermas Acumuladas</div>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#DC2626", marginTop: "3px" }}>
                      {selectedHolograma.mermasCant ?? selectedHolograma.cantidad_merma ?? selectedHolograma.cantidadMerma ?? 0} pzs
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748B" }}>{selectedHolograma.mermasRango || "N/A"}</div>
                  </div>

                  <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "10px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#059669", textTransform: "uppercase" }}>Saldo Disponible Actual</div>
                    <div style={{ fontSize: "15px", fontWeight: 900, color: "#059669", marginTop: "3px" }}>
                      {(selectedHolograma.invFinalCant !== undefined && selectedHolograma.invFinalCant !== null)
                        ? selectedHolograma.invFinalCant
                        : (selectedHolograma.cantidad_disponible ?? selectedHolograma.cantidadDisponible ?? selectedHolograma.invInicialCant ?? 0)} pzs
                    </div>
                    <div style={{ fontSize: "11px", color: "#059669", fontWeight: 600 }}>
                      {selectedHolograma.invFinalRango || selectedHolograma.invInicialRango || selectedHolograma.rango_completo || "N/A"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contenido del Modal con la sección exacta solicitada */}
              <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "24px" }}>



                {/* HISTORIAL ÚNICO DE MOVIMIENTOS DE HOLOGRAMAS (salidas por rango con su tipo de salida) */}
                <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px", borderBottom: "1px solid #E2E8F0", paddingBottom: "12px" }}>
                    <HistoryIcon style={{ color: "#059669", fontSize: 22 }} />
                    <div>
                      <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 800, color: "#1E293B" }}>
                        Historial de Movimientos de este Inventario ({movimientosDelLote.length})
                      </h4>
                      <span style={{ fontSize: "11.5px", color: "#64748B" }}>
                        Cada salida de hologramas aceptada por la Unidad de Inspección queda registrada aquí con su tipo de salida
                      </span>
                    </div>
                  </div>

                  {movimientosDelLote.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "28px 16px", background: "#F8FAFC", borderRadius: "10px", border: "1px dashed #CBD5E1" }}>
                      <LocalOfferIcon style={{ fontSize: 32, color: "#94A3B8", marginBottom: "6px" }} />
                      <p style={{ margin: 0, fontSize: "13px", color: "#64748B", fontWeight: 600 }}>
                        {(selectedHolograma.salidaCant || selectedHolograma.cantidad_utilizada) && Number(selectedHolograma.salidaCant || selectedHolograma.cantidad_utilizada) > 0
                          ? `Salida registrada: ${selectedHolograma.salidaRango || 'Folios'} (${selectedHolograma.salidaCant || selectedHolograma.cantidad_utilizada} pzs).`
                          : "Aún no se han registrado movimientos de salida o deducciones por envasado para este lote de hologramas."}
                      </p>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <thead>
                          <tr style={{ background: "#F1F5F9", color: "#475569", borderBottom: "1px solid #CBD5E1", textAlign: "left" }}>
                            <th style={{ padding: "8px 10px" }}>Fecha</th>
                            <th style={{ padding: "8px 10px" }}>Lote Terminado</th>
                            <th style={{ padding: "8px 10px" }}>Tipo de Salida</th>
                            <th style={{ padding: "8px 10px" }}>Rango Consumido</th>
                            <th style={{ padding: "8px 10px" }}>Cantidad</th>
                            <th style={{ padding: "8px 10px" }}>Mermas</th>
                            <th style={{ padding: "8px 10px" }}>Saldo Restante</th>
                            <th style={{ padding: "8px 10px" }}>Estatus UI</th>
                            <th style={{ padding: "8px 10px" }}>Observaciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {movimientosDelLote.map((mov, mIdx) => (
                            <tr key={mov.id || mIdx} style={{ borderBottom: "1px solid #E2E8F0" }}>
                              <td style={{ padding: "9px 10px", color: "#64748B", whiteSpace: "nowrap" }}>
                                {mov.fecha}
                              </td>
                              <td style={{ padding: "9px 10px", fontWeight: 700, color: "#1E293B" }}>
                                <span style={{ background: "#FAF5FF", border: "1px solid #E9D5FF", color: "#6B21A8", padding: "2px 6px", borderRadius: "4px", fontSize: "11px" }}>
                                  {mov.loteEnvasado || "Lote Envasado"}
                                </span>
                                {mov.marca && mov.marca !== "-" && (
                                  <div style={{ fontSize: "11px", color: "#64748B", marginTop: "2px" }}>{mov.marca}</div>
                                )}
                              </td>
                              <td style={{ padding: "9px 10px" }}>
                                <span style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  background: /merma|baja/i.test(mov.tipoSalida || "") ? "#FEF2F2" : "#EFF6FF",
                                  color: /merma|baja/i.test(mov.tipoSalida || "") ? "#DC2626" : "#1D4ED8",
                                  border: `1px solid ${/merma|baja/i.test(mov.tipoSalida || "") ? "#FECACA" : "#BFDBFE"}`
                                }}>
                                  {mov.tipoSalida || "-"}
                                </span>
                              </td>
                              <td style={{ padding: "9px 10px", fontWeight: 700, color: "#0F172A" }}>
                                {mov.rangoConsumido}
                              </td>
                              <td style={{ padding: "9px 10px", fontWeight: 800, color: "#D97706" }}>
                                {mov.cantidadConsumida} pzs
                              </td>
                              <td style={{ padding: "9px 10px", color: Number(mov.cantidadMermada) > 0 ? "#DC2626" : "#64748B", fontWeight: 600 }}>
                                {mov.cantidadMermada || 0}
                              </td>
                              <td style={{ padding: "9px 10px", color: "#059669", fontWeight: 700 }}>
                                {mov.saldoRestanteCant !== undefined && mov.saldoRestanteCant !== "-" ? `${mov.saldoRestanteCant} pzs` : "-"}
                              </td>
                                <td style={{ padding: "9px 10px" }}>
                                  <span style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "2px 7px",
                                    borderRadius: "10px",
                                    fontSize: "10.5px",
                                    fontWeight: 700,
                                    backgroundColor: mov.estatus === "aprobado" ? "#DCFCE7" : (mov.estatus === "rechazado" ? "#FEE2E2" : "#FEF3C7"),
                                    color: mov.estatus === "aprobado" ? "#15803D" : (mov.estatus === "rechazado" ? "#B91C1C" : "#B45309"),
                                    border: `1px solid ${mov.estatus === "aprobado" ? "#86EFAC" : (mov.estatus === "rechazado" ? "#FCA5A5" : "#FCD34D")}`
                                  }}>
                                    {mov.estatus === "aprobado" ? "✓ Aprobado (Descontado)" : (mov.estatus === "rechazado" ? "✕ Rechazado" : "⏳ En revisión UI")}
                                  </span>
                                  {mov.observaciones_correccion && (
                                    <div style={{ fontSize: "10px", color: "#DC2626", marginTop: "2px" }}>
                                      {mov.observaciones_correccion}
                                    </div>
                                  )}
                                </td>
                              <td style={{ padding: "9px 10px", color: "#64748B", fontSize: "11px", maxWidth: "200px" }}>
                                {mov.observaciones}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

              </div>

              {/* Footer del Modal */}
              <div
                style={{
                  padding: "14px 24px",
                  borderTop: "1px solid #E2E8F0",
                  display: "flex",
                  justifyContent: "flex-end",
                  background: "#F8FAFC",
                  borderBottomLeftRadius: "16px",
                  borderBottomRightRadius: "16px"
                }}
              >
                <button
                  type="button"
                  onClick={() => setIsMovimientosModalOpen(false)}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "8px",
                    border: "1px solid #CBD5E1",
                    background: "#FFFFFF",
                    color: "#475569",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  Cerrar
                </button>
              </div>

            </div>
          </div>,
          document.body
        )}


      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="history-section-header">
        <button onClick={() => setIsFormOpen(false)} className="btn-secondary-custom" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Volver al Historial</span>
        </button>
        <h2>Nuevo Registro de Inventario de Hologramas - Sección V</h2>
      </div>

      <div className="cards-container-grid">
        {/* ENLAZAR ENVASADO TERMINADO */}
        <section className="detail-card detail-card-accent-left-green">
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-green">
              <LayersIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Relación de Envasado & Identificación</h2>
              <p>Vincular holograma con lote terminado</p>
            </div>
          </header>

          <div className="custom-input-group">
            <select
              value={idEnvasadoTerminado}
              onChange={e => setIdEnvasadoTerminado(e.target.value)}
              className="custom-select-field"
              style={{ padding: "18px 16px 8px 16px" }}
            >
              <option value="manual">Ingreso Manual (Sin lote pre-registrado)</option>
              {terminadoRecords.map(r => (
                <option key={r.id} value={r.id}>
                  {r.id} - Lote: {r.loteEnvasado} ({r.marca} - {r.capacidad})
                </option>
              ))}
            </select>
            <label className="custom-input-label">Enlace Envasado Terminado (ID)</label>
          </div>

          <div className="species-grid" style={{ marginTop: 16 }}>
            <div className="custom-input-group">
              <input
                type="date"
                value={fechaMovimiento}
                onChange={e => setFechaMovimiento(e.target.value)}
                className="custom-input-field"
              />
              <label className="custom-input-label">Fecha del Movimiento</label>
            </div>

            <div className="custom-input-group">
              <input
                type="text"
                value={marca}
                onChange={e => setMarca(e.target.value)}
                className="custom-input-field"
                disabled={idEnvasadoTerminado !== "manual"}
              />
              <label className="custom-input-label">Marca Comercial</label>
            </div>
          </div>

          <div className="species-grid">
            <div className="custom-input-group">
              <input
                type="text"
                value={loteEnvasado}
                onChange={e => setLoteEnvasado(e.target.value)}
                className="custom-input-field"
                disabled={idEnvasadoTerminado !== "manual"}
              />
              <label className="custom-input-label">Lote de Envasado</label>
            </div>

            <div className="custom-input-group">
              <input
                type="text"
                value={capacidad}
                onChange={e => setCapacidad(e.target.value)}
                className="custom-input-field"
                disabled={idEnvasadoTerminado !== "manual"}
              />
              <label className="custom-input-label">Capacidad de la Botella</label>
            </div>
          </div>
        </section>

        {/* INVENTARIO INICIAL & ENTRADAS */}
        <section className="detail-card detail-card-accent-left-copper">
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-copper">
              <ConfirmationNumberIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Inventario Inicial & Entradas</h2>
              <p>Rangos numéricos correlativos asignados</p>
            </div>
          </header>

          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--text-color)", marginBottom: "8px" }}>INVENTARIO INICIAL</h3>
          <div className="species-grid">
            <div className="custom-input-group">
              <input
                type="text"
                value={invInicialRango}
                onChange={e => setInvInicialRango(e.target.value)}
                className="custom-input-field"
                placeholder="Ej. A0001 - A0500"
              />
              <label className="custom-input-label">Rango Inicial</label>
            </div>

            <div className="custom-input-group">
              <input
                type="number"
                value={invInicialCant}
                onChange={e => setInvInicialCant(e.target.value)}
                className="custom-input-field"
              />
              <label className="custom-input-label">Cantidad Inicial</label>
            </div>
          </div>

          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--agave-green)", marginBottom: "8px", marginTop: "20px" }}>ENTRADAS (NUEVA ASIGNACIÓN)</h3>
          <div className="species-grid">
            <div className="custom-input-group">
              <input
                type="text"
                value={entradaRango}
                onChange={e => setEntradaRango(e.target.value)}
                className="custom-input-field"
                placeholder="Ej. B0001 - B1000"
              />
              <label className="custom-input-label">Rango Entrada</label>
            </div>

            <div className="custom-input-group">
              <input
                type="number"
                value={entradaCant}
                onChange={e => setEntradaCant(e.target.value)}
                className="custom-input-field"
              />
              <label className="custom-input-label">Cantidad Entrada</label>
            </div>
          </div>
        </section>

        {/* SALIDAS, MERMAS E INVENTARIO FINAL */}
        <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1" }}>
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-green">
              <TrendingDownIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Consumo (Salidas), Mermas y Balance Final</h2>
              <p>Control de uso de hologramas y adhesión física en botella</p>
            </div>
          </header>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", marginTop: "12px" }}>
            {/* Column 1: Salidas */}
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--copper)", borderBottom: "1px solid var(--glass-border)", paddingBottom: "6px", marginBottom: "16px" }}>
                SALIDAS (APLICADOS)
              </h3>
              <div className="custom-input-group" style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  value={salidaRango}
                  onChange={e => setSalidaRango(e.target.value)}
                  className="custom-input-field"
                  placeholder="Ej. A0001 - A0450"
                />
                <label className="custom-input-label">Rango Consumido</label>
              </div>

              <div className="custom-input-group">
                <input
                  type="number"
                  value={salidaCant}
                  onChange={e => setSalidaCant(e.target.value)}
                  className="custom-input-field"
                />
                <label className="custom-input-label">Cantidad Consumida</label>
              </div>
            </div>

            {/* Column 2: Mermas */}
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--status-draft-color)", borderBottom: "1px solid var(--glass-border)", paddingBottom: "6px", marginBottom: "16px" }}>
                MERMAS (DAÑADOS)
              </h3>
              <div className="custom-input-group" style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  value={mermasRango}
                  onChange={e => setMermasRango(e.target.value)}
                  className="custom-input-field"
                  placeholder="Ej. A0451 - A0453"
                />
                <label className="custom-input-label">Rango Mermas</label>
              </div>

              <div className="custom-input-group">
                <input
                  type="number"
                  value={mermasCant}
                  onChange={e => setMermasCant(e.target.value)}
                  className="custom-input-field"
                />
                <label className="custom-input-label">Cantidad Mermada</label>
              </div>
            </div>

            {/* Column 3: Inventario Final */}
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--agave-green)", borderBottom: "1px solid var(--glass-border)", paddingBottom: "6px", marginBottom: "16px" }}>
                INVENTARIO FINAL
              </h3>
              <div className="custom-input-group" style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  value={invFinalRango}
                  onChange={e => setInvFinalRango(e.target.value)}
                  className="custom-input-field"
                  placeholder="Ej. A0454 - A0500"
                />
                <label className="custom-input-label">Rango Final</label>
              </div>

              <div className="custom-input-group">
                <input
                  type="number"
                  value={invFinalCant}
                  onChange={e => setInvFinalCant(e.target.value)}
                  className="custom-input-field"
                />
                <label className="custom-input-label">Cantidad Final</label>
              </div>
            </div>
          </div>

          <div className="custom-input-group" style={{ marginTop: 24 }}>
            <textarea
              value={observaciones}
              onChange={e => setObservaciones(e.target.value)}
              className="custom-input-field"
              style={{ height: "80px", resize: "none", padding: "14px" }}
              placeholder="Ej. Hologramas dañados en la aplicación del lote T-ENV-01. Mermas archivadas para revisión..."
            />
            <label className="custom-input-label">Observaciones</label>
          </div>
        </section>
      </div>

      <div className="button-row">
        <button type="button" className="btn-secondary-custom" onClick={() => setIsFormOpen(false)}>Volver</button>
        <button
          type="button"
          className="btn-primary-custom"
          onClick={() => handleSaveHologramas("revision")}
        >
          <CheckCircleIcon style={{ fontSize: 16 }} />
          <span>Registrar Sección V</span>
        </button>
      </div>
    </div>
  );
};

export default ControlHologramasSection;
